<script module lang="ts">
	import { rowActions } from '@repo/commons/components'

	export type ConfiguredTableRow = {
		id: string
		name: string
		schemaName: string
	}

	const ROW_ACTIONS = [rowActions.EDIT, rowActions.DELETE]
</script>

<script lang="ts">
	import { ActionButtonsIcons, DataTable, PageHeader } from '@repo/commons/components'
	import type { TableColumn, TableData } from '@repo/commons/models'
	import { DebouncedSearch, getTableRowId } from '@repo/commons/utils'
	import { t } from 'svelte-i18n'

	type Props = {
		tables: readonly ConfiguredTableRow[]
		loading: boolean
		sortable: boolean
		onConfigure: VoidFunction
		onOpen: (tableId: string) => void
		onDelete: (tableId: string) => void
	}

	const { tables, loading, sortable, onConfigure, onOpen, onDelete }: Props = $props()

	const search = new DebouncedSearch()

	const visibleTables = $derived(
		search
			.filterRows(tables, ({ name }) => name)
			.map((table) => ({ ...table, actions: ROW_ACTIONS }))
	)

	const emptyText = $derived(tables.length ? undefined : $t('InstanceReferenceTables.empty'))

	const ROW_ACTION_HANDLER: Partial<Record<rowActions, (tableId: string) => void>> = {
		[rowActions.EDIT]: onOpen,
		[rowActions.DELETE]: onDelete
	}

	const handleRowAction = (action: rowActions, tableId: string): void => {
		ROW_ACTION_HANDLER[action]?.(tableId)
	}

	const handleRowClick = ({ id }: TableData): void => {
		onOpen(String(id))
	}

	const columns = $derived([
		{ key: 'name', label: $t('ReferenceTablesList.name'), width: '30%', sortable },
		{ key: 'schemaName', label: $t('ReferenceTablesList.schema'), sortable },
		{
			key: 'actions',
			label: $t('Table.actions'),
			width: '120px',
			component: ActionButtonsIcons,
			componentProps: { onActionHandler: handleRowAction },
			sortable: false
		}
	] as unknown as TableColumn[])
</script>

<PageHeader
	title={$t('InstanceReferenceTables.title')}
	addButtonText={$t('InstanceReferenceTables.configure')}
	addButtonIcon={null}
	onAdd={onConfigure}
	onSearch={search.updateQuery}
/>

<DataTable
	data={visibleTables}
	{columns}
	{loading}
	{emptyText}
	clickableRows
	onRowClick={handleRowClick}
	getRowId={getTableRowId}
/>
